import { Component, Injector, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { UserAccountService } from '../../../common/service/user-account.service';
import { SelectionItem } from '../../../core/component/select/select.component';
import { LocalDate } from '../../../core/model/local-date';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MeterMediaType } from '../../model/const/enums';
import { Meter } from '../../model/meter';
import { ConsumptionDayService } from '../../service/consumption-day.service';
import { MeterModelService } from '../../service/meter-type.service';
import { CreateMeterDialogFormComponent } from '../create-meter-dialog-form/create-meter-dialog-form.component';

@Component({
    selector: 'app-rental-object-meter',
    templateUrl: './rental-object-meter.component.html',
    styleUrls: ['./rental-object-meter.component.scss'],
    providers: [ConsumptionDayService],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RentalObjectMeterComponent implements OnInit, OnDestroy {
  constructor(
    private _meterModelService: MeterModelService,
    private _fcf: FormControlExFactory,
    private _windowService: ActionWindowService,
    private _accountService: UserAccountService,
    private _injector: Injector
  ) {}

  // Accept AbstractControl for template binding; used internally as a FormGroup.
  private _form: FormGroup;
  @Input() set form(value: AbstractControl) {
    this._form = value as FormGroup;
  }
  get form(): FormGroup {
    return this._form;
  }
  @Input() meterType: MeterMediaType;
  private _propertyMeters: Meter[];
  @Input() set propertyMeters(meters: Meter[]) {
    this._propertyMeters = meters;
    this._meterSelection = null;
  }
  @Input() propertyId: string;

  private _sub: Subscription;
  private _accountsub: Subscription;

  allowNewMeter: boolean = false;
  modelSelection: SelectionItem[];

  ngOnDestroy(): void {
    this._sub?.unsubscribe();
    this._accountsub?.unsubscribe();
  }

  get showConsumption(): boolean {
    return this.form && this.form.controls['id'].value;
  }

  _meterSelection: SelectionItem[];
  get meterSelection(): SelectionItem[] {
    if (this._meterSelection) {
      return this._meterSelection;
    }
    if (!this._propertyMeters || !this.meterType) {
      return [];
    }
    let selection = this._propertyMeters.filter(m => m.meterType == this.meterType).map(m => new SelectionItem(m.name, m.id));
    selection.splice(0, 0, new SelectionItem('CONST.NONE', null));
    this._meterSelection = selection;
    return this.meterSelection;
  }

  private _dialogSub: Subscription;
  createMeter() {
    if (this._dialogSub) {
      this._dialogSub.unsubscribe();
    }
    this._dialogSub = this._windowService
      .open(CreateMeterDialogFormComponent, this._injector, null, {
        modelSelection: this.modelSelection,
        meterType: this.meterType,
        propertyId: this.propertyId,
      })
      .pipe(take(1))
      .subscribe(model => {
        if (model.meterType === this.meterType) {
          this._propertyMeters.push(model);
          this.meterSelection.push(new SelectionItem(model.name, model.id));
          this.form.controls['imdMeterId'].setValue(model.id);
          this.form.markAsDirty();
          this.form.markAllAsTouched();
        }
      });
  }

  get consumptionFilter(): { fromDate?: LocalDate; toDate?: LocalDate; meterId?: string; meterType?: MeterMediaType } {
    return {
      fromDate: new LocalDate(2000, 1, 1),
      toDate: LocalDate.today(),
      meterId: this.form.controls['id'].value,
      meterType: this.meterType,
    };
  }

  ngOnInit() {
    this._sub = this._meterModelService.getSelection(this.meterType).subscribe(res => {
      res.splice(0, 0, new SelectionItem('COMMON.UNKNOWN', null));
      this.modelSelection = res;
    });

    this._accountsub = this._accountService.current.subscribe({
      next: account => (this.allowNewMeter = account.hasAccess(ModuleNames.Consumption, Features.Consumption_MeterModel_Create)),
    });
  }
}
